import { useState, useRef } from "react";
import useGoBack from "hooks/useGoBack";
import SubHeader from "components/common/SubHeader";
import Blank from "components/common/Blank";
import useUserStore from "@store/useUserStore";
import Postcode from "components/common/Postcode";
import CommonModal from "components/common/CommonModal";
import ProfileAvatar from "components/common/ProfileAvatar";
import BottomSheet from "components/common/BottomSheet";
import {
    getUserAddressList,
    updateUserAddressList,
    updateUserData,
    uploadProfileImage,
    deleteProfileImage,
} from "api/user";
import { useSnackbar } from "contexts/SnackbarContext";

const UserEdit = () => {
    const goBack = useGoBack();
    const userInfo = useUserStore((state) => state.userInfo);
    const setUserInfo = useUserStore((state) => state.setUserInfo);
    const { showSuccess, showError } = useSnackbar();

    const [updateUserInfo, setUpdateUserInfo] = useState({
        nickname: userInfo.nickname,
        bio: userInfo.bio || "",
    });
    const [showImageSheet, setShowImageSheet] = useState(false);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const fileInputRef = useRef(null);

    const saveUserInfo = async () => {
        const res = await updateUserData(updateUserInfo);
        if (res.code === "0000") {
            setUserInfo({
                ...userInfo,
                ...updateUserInfo,
            });
            showSuccess("저장되었습니다");
            goBack();
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setIsUploadingImage(true);
        try {
            const res = await uploadProfileImage(file);
            if (res.profile_image_url) {
                setUserInfo({ ...userInfo, profile_image_url: res.profile_image_url });
                showSuccess("프로필 이미지가 변경되었습니다");
            }
        } catch (err) {
            const serverMsg = err?.response?.data?.detail || err?.response?.data?.message || err?.response?.data?.image?.[0];
            showError(serverMsg || "이미지 업로드에 실패했습니다");
        } finally {
            setIsUploadingImage(false);
            setShowImageSheet(false);
            e.target.value = "";
        }
    };

    const handleImageDelete = async () => {
        setIsUploadingImage(true);
        try {
            await deleteProfileImage();
            setUserInfo({ ...userInfo, profile_image_url: null });
            showSuccess("프로필 이미지가 삭제되었습니다");
        } catch {
            showError("이미지 삭제에 실패했습니다");
        } finally {
            setIsUploadingImage(false);
            setShowImageSheet(false);
        }
    };

    return (
        <div className="page-user-edit">
            <SubHeader title="내 정보 관리" />
            <Blank height={57} />
            <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleImageUpload}
            />
            <div className="profile-image-edit-section">
                <ProfileAvatar
                    imageUrl={userInfo.profile_image_url}
                    size={100}
                    onClick={() => setShowImageSheet(true)}
                />
                <button
                    className="profile-image-change-btn"
                    onClick={() => setShowImageSheet(true)}
                    disabled={isUploadingImage}
                >
                    {isUploadingImage ? "업로드 중..." : "사진 변경"}
                </button>
            </div>
            <div className="ue-section">
                <p className="ue-section-title">사용자 정보</p>
                <div className="input-wrap">
                    <div className="input-form">
                        <p className="label">닉네임</p>
                        <input
                            className="input"
                            placeholder="닉네임 입력"
                            value={updateUserInfo.nickname}
                            onChange={(e) =>
                                setUpdateUserInfo({
                                    ...updateUserInfo,
                                    nickname: e.target.value,
                                })
                            }
                        />
                    </div>
                    <div className="input-form">
                        <p className="label">
                            소개
                            <span className="char-count">
                                {updateUserInfo.bio.length}/150
                            </span>
                        </p>
                        <textarea
                            className="input bio-textarea"
                            placeholder="자기소개를 입력해주세요"
                            value={updateUserInfo.bio}
                            maxLength={150}
                            onChange={(e) =>
                                setUpdateUserInfo({
                                    ...updateUserInfo,
                                    bio: e.target.value,
                                })
                            }
                        />
                    </div>
                    <button
                        className="btn btn-purple500 btn-large"
                        onClick={saveUserInfo}
                    >
                        저장
                    </button>
                </div>
            </div>

            <BottomSheet
                isOpen={showImageSheet}
                onClose={() => setShowImageSheet(false)}
                height="auto"
            >
                <div className="image-action-list">
                    <button onClick={() => fileInputRef.current?.click()}>
                        앨범에서 선택
                    </button>
                    {userInfo.profile_image_url && (
                        <button className="delete" onClick={handleImageDelete}>
                            현재 사진 삭제
                        </button>
                    )}
                </div>
            </BottomSheet>
        </div>
    );
};

export default UserEdit;

export const AddressCard = ({ address, setAddressList }) => {
    const userInfo = useUserStore((state) => state.userInfo);
    const setUserInfo = useUserStore((state) => state.setUserInfo);
    const [isOpen, setIsOpen] = useState(false);
    const onRequestClose = () => {
        setIsOpen(false);
    };
    return (
        <>
            <div className="address-card-wrap">
                <div className="address-card-contents">
                    <div>
                        <p className="bold">
                            <span>{address.label}</span>
                            <span
                                className={
                                    address.is_primary
                                        ? "badge no-margin badge-outline-purple"
                                        : ""
                                }
                            >
                                {address.is_primary ? "대표" : ""}
                            </span>
                        </p>
                        <div className="address-info">
                            주소: {address.zipcode} {address.road_address}{" "}
                            {address.detail_address}
                        </div>
                    </div>
                    <div
                        className="address-edit-btn"
                        onClick={() => setIsOpen(true)}
                    >
                        <i className="icon icon-edit" />
                    </div>
                </div>
            </div>
            <CommonModal
                isOpen={isOpen}
                onRequestClose={onRequestClose}
                className={`address-modal-wrap`}
            >
                <Postcode
                    address={{
                        zipcode: address.zipcode,
                        roadAddress: address.road_address,
                        detailAddress: address.detail_address,
                        order: address.order,
                        is_primary: address.is_primary,
                        label: address.label,
                        id: address.id,
                    }}
                    saveAddress={(id, postcode, isPrimary) => {
                        if (!!id) {
                            if (isPrimary) {
                                setAddressList((prev) =>
                                    prev.map((item) => ({
                                        ...item,
                                        is_primary: item.id === id,
                                    }))
                                );
                            } else {
                                setAddressList((prev) =>
                                    prev.map((item) =>
                                        item.id === id
                                            ? { ...item, ...postcode }
                                            : item
                                    )
                                );
                            }

                            updateUserAddressList(id, postcode)
                                .then((res) => {
                                    setAddressList((prev) =>
                                        prev.map((item) =>
                                            item.id === id ? res : item
                                        )
                                    );
                                    setUserInfo({
                                        ...userInfo,
                                        addresses: userInfo.addresses.map(
                                            (addr) =>
                                                addr.id === id ? res : addr
                                        ),
                                    });
                                })
                                .catch(() => {
                                    getUserAddressList().then((res) => {
                                        if (res) setAddressList(res);
                                    });
                                });
                        }
                        onRequestClose();
                    }}
                />
            </CommonModal>
        </>
    );
};
